import {Button, Modal, message} from "antd";
import {FormEvent, useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import {
  useConfirmEmailChange,
  useSendEmailChangeOtp,
} from "../../../react-query-hooks/settings";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../payouts/v2/tabs/payout-settings-tab/otp-view/styles";
import styled, {createGlobalStyle} from "styled-components";

const StyledEmailOtpInput = styled(StyledOtpInput)`
  && {
    width: 6.2rem !important;
    height: 4.8rem !important;
    border-radius: 4px;
    font-size: 1.8rem;
    text-align: center;
  }

  &:focus {
    outline: none;
  }
`;

const VerifyEmailModalGlobalStyles = createGlobalStyle`
  .verify-email-modal {
    .ant-modal-content {
      border-radius: 8px;
    }

    .ant-modal-header {
      border-bottom: 1px solid #F0F0F0;
      padding: 20px 24px;
      margin: 0;
    }

    .ant-modal-body {
      padding: 20px 24px 24px;
    }
  }
`;

interface VerifyEmailModalProps {
  visible: boolean;
  currentEmail: string;
  newEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function VerifyEmailModal({
  visible,
  currentEmail,
  newEmail,
  onClose,
  onSuccess,
}: VerifyEmailModalProps) {
  const [otp, setOtp] = useState("");
  const confirmEmailMutation = useConfirmEmailChange();
  const sendOtpMutation = useSendEmailChangeOtp();

  const handleConfirm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (otp.length < 6) {
      message.error("Please enter the complete 6-digit OTP");
      return;
    }

    confirmEmailMutation.mutate(
      {otpCode: otp, email: newEmail},
      {
        onSuccess: () => {
          setOtp("");
          onSuccess();
          onClose();
        },
      }
    );
  };

  const handleResendOTP = () => {
    sendOtpMutation.mutate(undefined, {
      onSuccess: () => {
        setTimer(15);
        setOtp("");
      },
    });
  };

  const [timer, setTimer] = useState(15);

  useEffect(() => {
    if (!visible) {
      setOtp("");
      setTimer(15);
      return;
    }

    const timerCount = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerCount);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerCount);
  }, [visible]);

  const isConfirmDisabled = otp.length < 6 || confirmEmailMutation.isLoading;

  return (
    <>
      <VerifyEmailModalGlobalStyles />
      <Modal
        title="Verify email address"
        open={visible}
        onCancel={onClose}
        footer={null}
        closable={true}
        centered
        width={620}
        wrapClassName="verify-email-modal"
      >
        <form onSubmit={handleConfirm}>
          <div style={{width: "100%"}}>
            <p style={{fontSize: "1.6rem", margin: 0, lineHeight: "2.4rem"}}>
              Please enter the 6-digit OTP that has been sent to{" "}
              <strong>{currentEmail}</strong>
            </p>
            <div style={{marginTop: "20px"}}>
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                shouldAutoFocus
                inputType="tel"
                containerStyle={{display: "flex", gap: "16px"}}
                renderInput={(props) => <StyledEmailOtpInput {...props} />}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginTop: "36px",
                minHeight: "56px",
              }}
            >
              <div>
                {timer > 0 ? (
                  <p style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                    {timer} secs
                  </p>
                ) : (
                  <StyledTextButton type="text" size="small" onClick={handleResendOTP}>
                    <span className="text-underline">Resend OTP</span>
                  </StyledTextButton>
                )}
              </div>

              <Button
                htmlType="submit"
                disabled={isConfirmDisabled}
                loading={confirmEmailMutation.isLoading}
                style={{
                  borderRadius: 14,
                  ...(isConfirmDisabled
                    ? {
                        backgroundColor: "#F5F5F5",
                        borderColor: "#D9D9D9",
                        color: "#BFBFBF",
                      }
                    : {
                        backgroundColor: "#1C1726",
                        borderColor: "#1C1726",
                        color: "#fff",
                      }),
                }}
              >
                Confirm code
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
